/*
 * Copyright (c) 2024, Circle Internet Financial LTD All rights reserved.
 *
 * SPDX-License-Identifier: Apache-2.0
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {
  AnchorProvider,
  Provider,
  web3,
  utils,
  Idl,
  Program,
} from '@coral-xyz/anchor';
import { bs58 } from '@coral-xyz/anchor/dist/cjs/utils/bytes';
import { Connection, PublicKey } from '@solana/web3.js';
import { BytesLike, hexlify } from 'ethers';

import MessageTransmitter from '@/idl/message_transmitter.json';
import TokenMessengerMinter from '@/idl/token_messenger_minter.json';

interface FindProgramAddressResponse {
  publicKey: web3.PublicKey;
  bump: number;
}

interface PrioritizationFeeObject {
  slot: number;
  prioritizationFee: number;
}

interface Config {
  lockedWritableAccounts: PublicKey[];
}

export const getPrograms = (
  provider: AnchorProvider | Provider | undefined,
) => {
  const messageTransmitterProgram = new Program(
    MessageTransmitter as Idl,
    'CCTPmbSD7gX1bxKPAmg77w8oFzNFpaQiQUWD43TKaecd',
    provider,
  );
  const tokenMessengerMinterProgram = new Program(
    TokenMessengerMinter as Idl,
    'CCTPiPYPc6AsJuwueEnWgSgucamXDZwBd53dQ11YiKX3',
    provider,
  );

  return { messageTransmitterProgram, tokenMessengerMinterProgram };
};

export const getDepositForBurnPdas = (
  {
    messageTransmitterProgram,
    tokenMessengerMinterProgram,
  }: ReturnType<typeof getPrograms>,
  usdcAddress: PublicKey,
  destinationDomain: number,
) => {
  const messageTransmitterAccount = findProgramAddress(
    'message_transmitter',
    messageTransmitterProgram.programId,
  );
  const tokenMessengerAccount = findProgramAddress(
    'token_messenger',
    tokenMessengerMinterProgram.programId,
  );
  const tokenMinterAccount = findProgramAddress(
    'token_minter',
    tokenMessengerMinterProgram.programId,
  );
  const localToken = findProgramAddress(
    'local_token',
    tokenMessengerMinterProgram.programId,
    [usdcAddress],
  );
  const remoteTokenMessengerKey = findProgramAddress(
    'remote_token_messenger',
    tokenMessengerMinterProgram.programId,
    [destinationDomain.toString()],
  );
  const authorityPda = findProgramAddress(
    'sender_authority',
    tokenMessengerMinterProgram.programId,
  );

  return {
    messageTransmitterAccount,
    tokenMessengerAccount,
    tokenMinterAccount,
    localToken,
    remoteTokenMessengerKey,
    authorityPda,
  };
};

export function solanaAddressToHex(solanaAddress: string) {
  return hexlify(bs58.decode(solanaAddress) as BytesLike);
}

// Convenience wrapper for PublicKey.findProgramAddressSync
const findProgramAddress = (
  label: string,
  programId: PublicKey,
  extraSeeds: (string | number[] | Buffer | PublicKey)[] = [],
): FindProgramAddressResponse => {
  const seeds = [Buffer.from(utils.bytes.utf8.encode(label))];
  if (extraSeeds) {
    for (const extraSeed of extraSeeds) {
      if (typeof extraSeed === 'string') {
        seeds.push(Buffer.from(utils.bytes.utf8.encode(extraSeed)));
      } else if (Array.isArray(extraSeed)) {
        seeds.push(Buffer.from(extraSeed as number[]));
      } else if (Buffer.isBuffer(extraSeed)) {
        seeds.push(Buffer.from(extraSeed));
      } else {
        seeds.push(Buffer.from(extraSeed.toBuffer()));
      }
    }
  }
  const res = PublicKey.findProgramAddressSync(seeds, programId);
  return { publicKey: res[0], bump: res[1] };
};

export const getPrioritizationFees = async (connection: Connection) => {
  try {
    const publicKey = new PublicKey(
      'CCTPiPYPc6AsJuwueEnWgSgucamXDZwBd53dQ11YiKX3',
    );

    const config: Config = {
      lockedWritableAccounts: [publicKey],
    };

    const prioritizationFeeObjects =
      (await connection.getRecentPrioritizationFees(
        config,
      )) as PrioritizationFeeObject[];

    if (prioritizationFeeObjects.length === 0) {
      console.log('No prioritization fee data available.');
      return 300000;
    }

    // Extract slots and sort them
    const slots = prioritizationFeeObjects
      .map((feeObject) => feeObject.slot)
      .sort((a, b) => a - b);

    // Extract slots range
    const slotsRangeStart = slots[0];
    const slotsRangeEnd = slots[slots.length - 1];

    // Calculate the average including zero fees
    const averageFeeIncludingZeros =
      prioritizationFeeObjects.length > 0
        ? Math.floor(
            prioritizationFeeObjects.reduce(
              (acc, feeObject) => acc + feeObject.prioritizationFee,
              0,
            ) / prioritizationFeeObjects.length,
          )
        : 0;

    // Filter out prioritization fees that are equal to 0 for other calculations
    const nonZeroFees = prioritizationFeeObjects
      .map((feeObject) => feeObject.prioritizationFee)
      .filter((fee) => fee !== 0);

    // Calculate the average of the non-zero fees
    const averageFeeExcludingZeros =
      nonZeroFees.length > 0
        ? Math.floor(
            nonZeroFees.reduce((acc, fee) => acc + fee, 0) / nonZeroFees.length,
          )
        : 0;

    // Calculate the median of the non-zero fees
    const sortedFees = nonZeroFees.sort((a, b) => a - b);
    let medianFee = 0;
    if (sortedFees.length > 0) {
      const midIndex = Math.floor(sortedFees.length / 2);
      medianFee =
        sortedFees.length % 2 !== 0
          ? sortedFees[midIndex]
          : Math.floor((sortedFees[midIndex - 1] + sortedFees[midIndex]) / 2);
    }

    console.log(
      `Slots range examined from ${slotsRangeStart} to ${slotsRangeEnd}`,
    );
    console.log('nonZeroFees', nonZeroFees);
    console.log(
      '====================================================================================',
    );

    // You can use averageFeeIncludingZeros, averageFeeExcludingZeros, and medianFee in your transactions script
    console.log(
      ` 💰 Average Prioritization Fee (including slots with zero fees): ${averageFeeIncludingZeros} micro-lamports.`,
    );
    console.log(
      ` 💰 Average Prioritization Fee (excluding slots with zero fees): ${averageFeeExcludingZeros} micro-lamports.`,
    );
    console.log(
      ` 💰 Median Prioritization Fee (excluding slots with zero fees): ${medianFee} micro-lamports.`,
    );
    // still transactions are not being minted. setting threshold
    return medianFee > 15000 ? medianFee : 300000;
  } catch (error) {
    console.error('Error fetching prioritization fees:', error);
  }
  return 300000;
};
